export interface LibraryItem {
  file: string;
  /** Optional display-only WebP; insertion always uses the original file. */
  thumbnail?: string;
  category: string;
  /** Additional category memberships; the original category remains primary. */
  categories?: string[];
  title?: string;
  name?: string;
  keywords?: string[];
}
export interface LibraryCategory {
  id: string;
  title: string;
}
export interface ImageLibrary {
  categories: LibraryCategory[];
  items: LibraryItem[];
}
/** Catalog thumbnails must stay inside the clipart base, with no URL syntax. */
export function isLibraryThumbnailPath(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value === value.trim() &&
    !value.includes("..") &&
    /^(?:[\w-]+\/)*[\w.-]+\.webp$/i.test(value)
  );
}
/** Select display metadata only; legacy/invalid metadata keeps the original URL. */
export function libraryPreviewUrl(item: LibraryItem, base: string): string {
  return (
    base + (isLibraryThumbnailPath(item.thumbnail) ? item.thumbnail : item.file)
  );
}
/** The same memberships drive filtering, search and category hints. */
export function libraryItemCategories(item: LibraryItem): string[] {
  return [
    ...new Set([
      item.category,
      ...(Array.isArray(item.categories) ? item.categories : []),
    ]),
  ].filter((id) => typeof id === "string" && !!id && id !== "all");
}
const keywords: Record<string, string[]> = {
  animals: ["животное", "животные", "зверь", "звери", "живой мир"],
  backgrounds: ["фон", "фоны", "пейзаж", "сцена", "задний план"],
  pets: ["домашние животные", "питомец", "питомцы"],
  farm_animals: ["ферма", "фермерские животные", "домашние животные"],
  forest_animals: ["лесные животные", "лес", "лесной"],
  jungle_animals: ["джунгли", "дикие животные", "сафари"],
  winter: ["зима", "зимний", "зимние"],
  autumn: ["осень", "осенний", "осенние"],
  beach: ["пляж", "пляжный", "пляжные"],
  underwater: ["подводный мир", "под водой", "подводный", "подводные"],
  environment: ["природа", "окружение", "среда"],
  food: ["еда", "фрукт", "фрукты", "овощ", "овощи"],
  space: ["космос", "космический", "космические"],
  other: ["прочее", "разное"],
};
export function transliterate(value: string) {
  const map: Record<string, string> = {
    а: "a",
    б: "b",
    в: "v",
    г: "g",
    д: "d",
    е: "e",
    ё: "e",
    ж: "zh",
    з: "z",
    и: "i",
    й: "y",
    к: "k",
    л: "l",
    м: "m",
    н: "n",
    о: "o",
    п: "p",
    р: "r",
    с: "s",
    т: "t",
    у: "u",
    ф: "f",
    х: "h",
    ц: "ts",
    ч: "ch",
    ш: "sh",
    щ: "sch",
    ъ: "",
    ы: "y",
    ь: "",
    э: "e",
    ю: "yu",
    я: "ya",
  };
  return value
    .toLowerCase()
    .split("")
    .map((c) => map[c] ?? c)
    .join("")
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
}
export function filterLibrary(
  library: ImageLibrary,
  category: string,
  query: string,
) {
  const raw = query
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/[^a-zа-яё0-9 ]+/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  const parts = raw
    .split(" ")
    .filter(Boolean)
    .map((part) => [part, transliterate(part).replace(/_/g, " ")]);
  return library.items.filter((item) => {
    const memberships = libraryItemCategories(item);
    if (category !== "all" && !memberships.includes(category)) return false;
    if (!parts.length) return !query.trim();
    const value = [
      item.file,
      item.title,
      item.name,
      ...(item.keywords ?? []),
      ...memberships.flatMap((id) => [
        ...(keywords[id] ?? []),
        library.categories.find((c) => c.id === id)?.title,
      ]),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    const haystack = (value + " " + transliterate(value))
      .replace(/[_-]+/g, " ")
      .replace(/[^a-zа-яё0-9 ]+/gi, " ")
      .replace(/\s+/g, " ");
    return parts.every((variants) =>
      variants.some((p) => haystack.includes(p)),
    );
  });
}
